import * as React from 'react'

import { clsx } from 'clsx'

import {
  Card,
  CopyIcon,
  DropdownMenu,
  Heading,
  IconButton,
  EllipsisIcon,
  PencilIcon,
  Slot,
  Tag,
  Text,
  TrashIcon,
} from '@buffer-mono/popcorn'
import type { HeadingProps, TextProps } from '@buffer-mono/popcorn'

import styles from './TemplateCard.module.css'

import type { JSX } from 'react'

type TemplateOwner = 'mine' | 'team' | 'buffer'

type TemplateCardData = {
  color: string
  description: string
  emoji?: string
  tags: string[]
  title: string
  owner?: TemplateOwner
}

const TemplateCardContext = React.createContext<TemplateCardData | null>(null)

function useTemplateCardContext(): TemplateCardData {
  const context = React.useContext(TemplateCardContext)
  if (!context) {
    throw new Error(
      'TemplateCard sub-components must be used within TemplateCard',
    )
  }
  return context
}

type RootProps = {
  template: TemplateCardData
  className?: string
  children: React.ReactNode
}

const Root = ({ template, className, children }: RootProps): JSX.Element => {
  const articleStyle: React.CSSProperties & Record<`--${string}`, string> = {
    '--template-color': template.color,
  }

  return (
    <TemplateCardContext.Provider value={template}>
      <Card asChild>
        <article
          style={articleStyle}
          className={clsx(styles.card, className)}
          aria-label={template.title}
        >
          {children}
        </article>
      </Card>
    </TemplateCardContext.Provider>
  )
}

Root.displayName = 'TemplateCard'

type IconProps = {
  size?: 'md' | 'lg'
  className?: string
}

const Icon = ({ size = 'lg', className }: IconProps): JSX.Element => {
  const template = useTemplateCardContext()

  return (
    <div className={clsx(styles.icon, className)} data-size={size}>
      {template.emoji || '📒'}
    </div>
  )
}

Icon.displayName = 'TemplateCard.Icon'

type TitleProps = {
  size?: HeadingProps['size']
  /** Heading level, so a card fits the hierarchy of the section around it. */
  as?: Extract<HeadingProps['as'], 'h2' | 'h3' | 'h4'>
  className?: string
}

const Title = ({
  size = 'md',
  as = 'h3',
  className,
}: TitleProps): JSX.Element => {
  const template = useTemplateCardContext()

  return (
    <Heading as={as} size={size} className={clsx(styles.title, className)}>
      {template.title}
    </Heading>
  )
}

Title.displayName = 'TemplateCard.Title'

type DescriptionProps = Omit<TextProps, 'children'> & {
  /** Maximum number of visible lines. Reduced by 1 when tags are present. */
  maxLines?: number
  className?: string
}

const Description = ({
  maxLines = 3,
  size = 'md',
  className,
}: DescriptionProps): JSX.Element | null => {
  const template = useTemplateCardContext()
  const style: React.CSSProperties & Record<`--${string}`, number> = {
    '--line-clamp-base': maxLines,
  }

  if (!template.description) {
    return null
  }

  return (
    <Text
      as="p"
      color="subtle"
      size={size}
      className={clsx(styles.description, className)}
      style={style}
    >
      {template.description}
    </Text>
  )
}

Description.displayName = 'TemplateCard.Description'

type TagsProps = {
  customTags?: string[]
  className?: string
}

const tagClassNames: Partial<Record<string, string>> = {
  Featured: styles['tag-Featured'],
  New: styles['tag-New'],
}

const Tags = ({
  customTags = [],
  className,
}: TagsProps): JSX.Element | null => {
  const template = useTemplateCardContext()

  if (customTags.length === 0 && template.tags.length === 0) {
    return null
  }

  return (
    <div className={clsx(styles.tags, className)}>
      {customTags.map((tag) => (
        <Text
          key={`custom-${tag}`}
          as="span"
          size="sm"
          color="subtle"
          weight="medium"
          className={styles.campaignLabel}
          translate="no"
        >
          {tag}
        </Text>
      ))}
      {template.tags.map((tag) => (
        <Tag
          key={`template-${tag}`}
          className={clsx(styles.tag, tagClassNames[tag])}
        >
          {tag}
        </Tag>
      ))}
    </div>
  )
}

Tags.displayName = 'TemplateCard.Tags'

type ClickableProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean
}

const Clickable = React.forwardRef<HTMLButtonElement, ClickableProps>(
  ({ asChild, className, ...props }, ref) => {
    const template = useTemplateCardContext()
    const Element = asChild ? Slot.Slot : 'button'
    return (
      <Element
        ref={ref}
        className={clsx(styles.clickable, className)}
        aria-label={`Open template ${template.title}`}
        {...(!asChild && { type: 'button' })}
        {...props}
      />
    )
  },
)

Clickable.displayName = 'TemplateCard.Clickable'

const RIBBON_CONFIG: Record<
  Exclude<TemplateOwner, 'buffer'>,
  { label: string }
> = {
  mine: { label: 'Only me' },
  team: { label: 'My team' },
}

type RibbonProps = {
  className?: string
}

const Ribbon = ({ className }: RibbonProps): JSX.Element | null => {
  const template = useTemplateCardContext()

  if (!template.owner || template.owner === 'buffer') {
    return null
  }

  const config = RIBBON_CONFIG[template.owner]

  return (
    <Tag className={clsx(styles.tag, styles.ribbon, className)}>
      {config.label}
    </Tag>
  )
}

Ribbon.displayName = 'TemplateCard.Ribbon'

type ActionsProps = {
  canEdit?: boolean
  canDelete?: boolean
  canDuplicate?: boolean
  onEdit?: () => void
  onDelete?: () => void
  onDuplicate?: () => void
  className?: string
}

const Actions = ({
  canEdit = false,
  canDelete = false,
  canDuplicate = true,
  onEdit,
  onDelete,
  onDuplicate,
  className,
}: ActionsProps): JSX.Element | null => {
  const template = useTemplateCardContext()

  if (!canEdit && !canDelete && !canDuplicate) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton
          variant="tertiary"
          label={`Template actions ${template.title}`}
          className={clsx(styles.overflowTrigger, className)}
          onClick={(e) => e.stopPropagation()}
        >
          <EllipsisIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <DropdownMenu.Item onSelect={onEdit}>
            <PencilIcon />
            Edit
          </DropdownMenu.Item>
        ) : null}
        {canDuplicate ? (
          <DropdownMenu.Item onSelect={onDuplicate}>
            <CopyIcon />
            Duplicate
          </DropdownMenu.Item>
        ) : null}
        {canDelete ? (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onSelect={onDelete} variant="critical">
              <TrashIcon />
              Delete
            </DropdownMenu.Item>
          </>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

Actions.displayName = 'TemplateCard.Actions'

export const TemplateCard = Object.assign(Root, {
  Actions,
  Clickable,
  Description,
  Icon,
  Ribbon,
  Tags,
  Title,
})

export type { RootProps as TemplateCardProps, TemplateCardData, TemplateOwner }
